// THE ONE CLIENT TRACKING DOOR (Founder 04/10/26: "השאלונים לא מתעדכנים מאז המעבר לדפי תלת מימד … תתקן מהשורש, רלוונטי לכל הדפים").
// Visit id (ex_sid), Google click ids (ex_clickid) and questionnaire logging (→ POST /api/questionnaire → he_questionnaire_logs,
// read by /admin/analytics via tracker_session_id). Used by the React site (lib/trackerSession.ts, components/Tracker.tsx,
// components/QuestionnaireWizard.tsx) AND inlined into the standalone 3D journey as window.ExTrack (lib/heMsgBrowser.ts →
// exTrackBrowserScript, x3d-assets/journey2.html __EXTRACK__). Plain browser JS, no imports — never copy a function out of here.
// Until 04/10 the 3D page carried its own copy of sid/clickIds and never logged its quiz → he_questionnaire_logs fell 2,173 → 98/day.

const SID_KEY = 'ex_sid', SID_TS_KEY = 'ex_sid_ts', SESSION_MAX_MS = 30 * 60 * 1000; // 30-min inactivity window
const CLICKID_KEY = 'ex_clickid', CLICKID_MAX_MS = 30 * 864e5;

/** The ONE tracker session id — created if missing (a lead/log sender may fire before the pageview). */
export function getSessionId() {
  if (typeof window === 'undefined') return '';
  try {
    const now = Date.now(), last = parseInt(localStorage.getItem(SID_TS_KEY) || '0', 10);
    let sid = localStorage.getItem(SID_KEY) || '';
    if (!sid || now - last > SESSION_MAX_MS) { sid = 's_' + now.toString(36) + Math.random().toString(36).slice(2, 10); localStorage.setItem(SID_KEY, sid); }
    localStorage.setItem(SID_TS_KEY, String(now));
    return sid;
  } catch (_) { return 's_' + Math.random().toString(36).slice(2, 12); }
}

/** Google Ads click ids. A click id in THIS url (a new ad click) wins — Google attributes to the latest click; otherwise the
 *  stored one (≤30 days) carries the attribution to later pages. A click id opened from our own admin is never taken. */
export function getClickIds() {
  const empty = { gclid: null, wbraid: null, gbraid: null };
  if (typeof window === 'undefined') return empty;
  try {
    let st = JSON.parse(localStorage.getItem(CLICKID_KEY) || 'null') || empty;
    // no `at` = a legacy entry (pre-04/10 copies never stamped one) → it never expired: one real customer's gclid rode the
    // Founder's phone for 6 weeks (06/10/26 audit). Undated = expired.
    if ((st.gclid || st.wbraid || st.gbraid) && (!st.at || Date.now() - Number(st.at) > CLICKID_MAX_MS)) { st = empty; localStorage.removeItem(CLICKID_KEY); }
    const p = new URLSearchParams(location.search), fromAdmin = /experts\.co\.il\/admin\b/.test(document.referrer || '');
    const fr = fromAdmin ? empty : { gclid: p.get('gclid'), wbraid: p.get('wbraid'), gbraid: p.get('gbraid') };
    if (fr.gclid || fr.wbraid || fr.gbraid) { // a fresh ad click replaces the stored set as a whole (never mix two clicks' ids)
      const m = { gclid: fr.gclid || null, wbraid: fr.wbraid || null, gbraid: fr.gbraid || null };
      if (m.gclid !== st.gclid || m.wbraid !== st.wbraid || m.gbraid !== st.gbraid) localStorage.setItem(CLICKID_KEY, JSON.stringify({ ...m, at: Date.now() }));
      return m;
    }
    return { gclid: st.gclid || null, wbraid: st.wbraid || null, gbraid: st.gbraid || null };
  } catch (_) { return empty; }
}

/** One questionnaire event → /api/questionnaire (he_questionnaire_logs). p = { wizardId, categorySlug, articleId?, version?,
 *  questionId, questionText?, answerValue?, answerLabel?, freeText?, isDropoff?, eventType: 'view'|'answer'|'dropoff' }.
 *  The tracker session id is attached here (always), so every surface's answers land on the visit in /admin/analytics. */
export async function questionnaireLog(p) {
  try {
    let trackerSessionId = null;
    try { trackerSessionId = getSessionId() || null; } catch (_) {}
    const body = JSON.stringify({
      sessionId: p.wizardId, trackerSessionId, categorySlug: p.categorySlug, articleId: p.articleId || null, version: p.version || '1.0',
      questionId: p.questionId, questionText: p.questionText || null, answerValue: p.answerValue == null ? null : String(p.answerValue).slice(0, 250),
      answerLabel: p.answerLabel == null ? null : String(p.answerLabel).slice(0, 250), freeText: p.freeText || null, isDropoff: !!p.isDropoff,
      eventType: p.eventType || (p.isDropoff ? 'dropoff' : 'answer'),
    });
    const send = () => fetch('/api/questionnaire', { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body });
    let r = await send().catch(() => null);
    if (!r || r.status >= 500) { await new Promise(res => setTimeout(res, 1500)); r = await send().catch(() => null); } // one retry on 5xx
    return !!(r && r.ok);
  } catch (_) { return false; }
}

/** Did a person touch this page? (pointer / touch / scroll / key since load) → client.interacted on every beacon → the visit's
 *  bot score (lib/botScore.ts; 06/10/26). The first call installs passive listeners; later calls just read the flag. */
let _human = false, _humanOn = false;
export function interacted() {
  if (typeof window === 'undefined') return false;
  if (!_humanOn) {
    _humanOn = true;
    const on = () => { _human = true; for (const e of ['pointerdown', 'touchstart', 'scroll', 'keydown', 'wheel']) removeEventListener(e, on, true); };
    for (const e of ['pointerdown', 'touchstart', 'scroll', 'keydown', 'wheel']) addEventListener(e, on, { passive: true, capture: true });
  }
  return _human;
}

/** A questionnaire instance id (he_questionnaire_logs.session_id) — one per wizard mount / 3D page load. */
export function newWizardId() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
